import {Theme} from '@mui/material/styles';
import {
  useMediaQuery,
  useTheme,
  Breakpoint,
  Box,
  Typography,
} from '@mui/material';
import {useState, useRef, useEffect, useMemo} from 'react';
import {useFFmpeg} from './mediahooks';
// import RecordRTC from 'recordrtc';

export const useIsLargeDevice = (breakPoint: Breakpoint | number = 'sm') => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up(breakPoint));
};

export const useIsCustomWidth = (breakPoint: Breakpoint | number) => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(breakPoint));
};

export const useWrappedTheme = (): Theme => {
  const theme = useTheme();
  return theme;
};

type BreakpointOrNull = Breakpoint | null;

export const useWidth = () => {
  const theme: Theme = useTheme();
  const keys: readonly Breakpoint[] = [...theme.breakpoints.keys].reverse();
  return (
    keys.reduce((output: BreakpointOrNull, key: Breakpoint) => {
      const matches = useMediaQuery(theme.breakpoints.up(key));
      return !output && matches ? key : output;
    }, null) || 'xs'
  );
};

interface componentTimerParams {
  callback: () => void;
  onTimerStart?: () => void;
  maxTime?: number;
}
export const useComponentTimer = ({
  callback,
  maxTime = 3,
  onTimerStart,
}: componentTimerParams) => {
  const theme = useTheme();
  const [timer, setTimer] = useState(maxTime);
  const [initializeTimer, setInitializeTimer] = useState(false);
  // const [timerCompleted, setTimerCompleted] = useState(false);
  const interval = useRef<any>();

  useEffect(() => {
    const handler = () => {
      if (interval.current) {
        clearInterval(interval.current);
        interval.current = undefined;
        setTimer(3);
        setInitializeTimer(false);
      }
    };
    if (window) {
      window?.addEventListener('click', handler);
    }
    return () => {
      window?.removeEventListener('click', handler);
      return;
    };
  }, []);

  useEffect(() => {
    function handleTimer() {
      if (onTimerStart) {
        onTimerStart();
      }
      interval.current = setInterval(() => {
        setTimer(count => count - 1);
      }, 1000);
    }

    if (timer <= 0 && interval.current) {
      clearInterval(interval.current);

      setInitializeTimer(false);
      if (callback) {
        callback();
      }
      interval.current = undefined;
      // setTimerCompleted(true);
    }
    if (initializeTimer && !interval.current) {
      handleTimer();
    }
  }, [initializeTimer, timer]);

  return {
    Timer: (
      <>
        {initializeTimer && (
          <Box
            sx={{
              background: '#F3F7FACC',
              boxShadow: 1,
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              left: 0,
              m: 'auto',
              borderRadius: 4,
              zIndex: 2,
              width: 240,
              height: 125,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box position={'relative'} textAlign={'center'} width={'100%'}>
              <Typography
                fontSize={64}
                color={'gray.dark'}
                lineHeight={'64px'}
                variant="subtitle1"
              >
                {timer}
              </Typography>
              <Typography variant="subtitle2" color={'text.primary'}>
                Click anywhere to Cancel
              </Typography>
            </Box>
          </Box>
        )}
      </>
    ),
    startTimer: () => {
      setTimer(maxTime);
      setTimeout(() => {
        interval.current = undefined;
        setInitializeTimer(true);
      }, 1);
    },
    timerCompleted: !initializeTimer,
  };
};

export const useMediaStream = ({
  onMediaPermission,
  ...params
}: {
  videoConfig: MediaTrackConstraints | boolean;
  audioConfig: MediaTrackConstraints | boolean;
  recordingDuration?: number;
  onRecordingStop?: () => void;
  streamRecorder: any;
  onMediaPermission?: any;
}) => {
  const videoRef = useRef<any>(null);
  const recordRef = useRef<any>(null);
  const [trackRestartnumber, setTrackRestartnumber] = useState(0);
  const {compressVideo} = useFFmpeg();

  const [blob, setBlob] = useState(null);
  const [compressedRecordedBlob, setCompressedRecordedBlob] = useState(null);

  const getStream = async () => {
    return mediaStream;
  };
  const preload = async () => {
    // await loadFFmpeg();
  };
  useEffect(() => {
    preload();
  }, []);

  const startWebcam = async (stream?: any) => {
    if (!stream) {
      stream = await mediaStream;
    }
    if (stream && videoRef?.current) {
      videoRef.current.srcObject = stream;
      recordRef.current = await params.streamRecorder(stream);
      // recordRef.current = new RecordRTC(stream, {
      //   type: 'video',
      //   disableLogs: true,
      // });
    }
    return;
  };

  const toggleBackgroundBlur = async () => {
    const stream = await getStream();
    if (!stream) {
      return;
    }
    const [track] = stream.getVideoTracks();
    const capabilities: any = track.getCapabilities();
    console.log('🚀 ~ toggleBackgroundBlur ~ capabilities:', capabilities);

    if (capabilities?.backgroundBlur?.length === 2) {
      const settings: any = track.getSettings();
      const constraints: any = {
        backgroundBlur: !settings.backgroundBlur,
      };
      // Request operating system to toggle background blur.
      await track.applyConstraints(constraints);
    }
  };

  const mediaStream = useMemo(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('getUserMedia is not supported in this environment.');
        throw new Error('getUserMedia is not supported in this environment.');
      }
      // Stop the current media stream (if any)
      // if (videoRef?.current?.srcObject) {
      //   videoRef.current.srcObject
      //     .getTracks()
      //     .forEach((track: any) => track.stop());
      // }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: params.audioConfig,
      });
      if (trackRestartnumber > 0) {
        startWebcam(stream);
      }
      onMediaPermission &&
        onMediaPermission({video: true, audio: params.audioConfig});
      return stream;
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        console.error(error.message);
      } else {
        // Handle other errors
        console.error('Error accessing media devices:', error);
      }
      onMediaPermission && onMediaPermission({});
      return null;
    }
  }, [trackRestartnumber]);

  const stopVideoStream = async () => {
    const stream = await mediaStream;
    stream?.getTracks().forEach((track: any) => {
      track.stop();
    });
  };

  // const displayStream = useMemo(async () => {
  //   return await navigator.mediaDevices.getDisplayMedia({
  //     video: {
  //       ...videoConstraints,
  //     },
  //     audio: true,
  //   });
  // }, []);

  const startRecording = async () => {
    const stream: any = await mediaStream;
    recordRef.current = await params.streamRecorder(stream);
    if (params.recordingDuration) {
      recordRef.current.setRecordingDuration(
        params.recordingDuration * 1000,
        recordingStopCB
      );
    }
    recordRef.current.startRecording();
  };
  const recordingStopCB = async () => {
    let blo = recordRef.current.getBlob();
    const newBlob: any = new Blob([blo], {type: 'video/mp4'});
    console.log(newBlob);
    compressVideo(blo)
      .then((ffmBlob: any) => {
        console.log(ffmBlob);
        setCompressedRecordedBlob(ffmBlob);
      })
      .catch(console.error);
    setBlob(newBlob);
    await stopVideoStream();
    if (params.onRecordingStop) {
      params.onRecordingStop();
    }
  };
  const stopRecording = async () => {
    recordRef.current.stopRecording(async () => {
      await recordingStopCB();
    });
  };
  const reset = async () => {
    await recordRef.current.reset();
    setBlob(null);
    setCompressedRecordedBlob(null);
    setTrackRestartnumber((s: number) => s + 1);
  };

  return {
    videoRef,
    mediaStream,
    startRecording,
    stopRecording,
    recordedBlob: blob,
    recordRef,
    compressedRecordedBlob,
    stopVideoStream,
    startWebcam,
    getStream,
    reset,
    toggleBackgroundBlur,
  };
};
